import { setExpiredSession } from './expiredSession';

/**
 * Manejador unificado para la expiración de sesión
 * Funciona tanto para autenticación tradicional como para ADF
 */
export class SessionManager {
  private static instance: SessionManager;
  private tokenCheckInterval: NodeJS.Timeout | null = null;
  private readonly CHECK_INTERVAL = 60000; // Verificar cada minuto

  private constructor() {}

  public static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  /**
   * Iniciar el monitoreo de la sesión
   */
  public startSessionMonitoring(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }

    this.tokenCheckInterval = setInterval(() => {
      this.checkSessionValidity();
    }, this.CHECK_INTERVAL);

    // Verificar inmediatamente al iniciar
    this.checkSessionValidity();
  }

  /**
   * Detener el monitoreo de la sesión
   */
  public stopSessionMonitoring(): void {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
      this.tokenCheckInterval = null;
    }
  }

  /**
   * Verificar si la sesión actual es válida
   */
  private checkSessionValidity(): void {
    const isLoggedIn = localStorage.getItem('logged') === 'true';
    const usedAdfAuth = localStorage.getItem('used_adf_auth') === 'true';

    if (!isLoggedIn) {
      return; // No hay sesión activa
    }

    if (usedAdfAuth) {
      this.checkAdfSessionValidity();
    } else {
      this.checkTraditionalSessionValidity();
    }
  }

  /**
   * Verificar validez de sesión ADF
   */
  private checkAdfSessionValidity(): void {
    const accessToken = localStorage.getItem('access');
    const refreshToken = localStorage.getItem('refresh');

    if (!accessToken || !refreshToken) {
      console.log('Tokens ADF no encontrados, cerrando sesión');
      this.handleSessionExpiration();
      return;
    }

    // Para ADF, MSAL maneja automáticamente la renovación de tokens
    // Solo verificamos que los tokens JWT del backend sigan siendo válidos
    this.verifyJwtTokenValidity(accessToken);
  }

  /**
   * Verificar validez de sesión tradicional
   */
  private checkTraditionalSessionValidity(): void {
    const accessToken = localStorage.getItem('access');
    const refreshToken = localStorage.getItem('refresh');

    if (!accessToken || !refreshToken) {
      console.log('Tokens tradicionales no encontrados, cerrando sesión');
      this.handleSessionExpiration();
      return;
    }

    this.verifyJwtTokenValidity(accessToken);
  }

  /**
   * Verificar si un token JWT es válido (no expirado)
   */
  private verifyJwtTokenValidity(token: string): void {
    try {
      // Decodificar el payload del JWT (sin verificar la firma)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < currentTime) {
        console.log('Token JWT expirado, intentando renovar');
        this.attemptTokenRefresh();
      }
    } catch (error) {
      console.error('Error verificando token JWT:', error);
      this.handleSessionExpiration();
    }
  }

  /**
   * Intentar renovar el token
   */
  private async attemptTokenRefresh(): Promise<void> {
    const usedAdfAuth = localStorage.getItem('used_adf_auth') === 'true';

    try {
      if (usedAdfAuth) {
        // Para ADF, intentar obtener un nuevo token usando MSAL
        await this.refreshAdfToken();
      } else {
        // Para autenticación tradicional, usar el endpoint de refresh
        await this.refreshTraditionalToken();
      }
    } catch (error) {
      console.error('Error renovando token:', error);
      this.handleSessionExpiration();
    }
  }

  /**
   * Renovar token ADF usando MSAL
   */
  private async refreshAdfToken(): Promise<void> {
    // Esta función se integrará con el AzureAuthProvider
    // Por ahora, delegamos al sistema de manejo de expiración existente
    console.log('Renovación de token ADF delegada a MSAL');
  }

  /**
   * Renovar token tradicional
   */
  private async refreshTraditionalToken(): Promise<void> {
    const serverUrl = localStorage.getItem('server_url');
    const refreshToken = localStorage.getItem('refresh');

    if (!serverUrl || !refreshToken) {
      throw new Error('Datos necesarios para renovación no disponibles');
    }

    const response = await fetch(`${serverUrl}/api/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (!response.ok) {
      throw new Error('Error renovando token tradicional');
    }

    const data = await response.json();
    localStorage.setItem('access', data.access);
    localStorage.setItem('refresh', data.refresh);
    
    console.log('Token tradicional renovado exitosamente');
  }

  /**
   * Manejar expiración de sesión
   */
  private handleSessionExpiration(): void {
    console.log('Sesión expirada, cerrando sesión del usuario');
    
    // Limpiar localStorage
    localStorage.removeItem('logged');
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('used_adf_auth');
    localStorage.removeItem('email');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    localStorage.removeItem('username');

    // Detener monitoreo
    this.stopSessionMonitoring();

    // Usar el mecanismo existente de expiración
    setExpiredSession();
  }

  /**
   * Limpiar sesión manualmente (para logout)
   */
  public clearSession(): void {
    this.stopSessionMonitoring();
    this.handleSessionExpiration();
  }
}

// Exportar instancia singleton
export const sessionManager = SessionManager.getInstance();
